import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { IFirstLastLoopResponse, ILoopDetail, ILoopDetailTable } from '../../../widget/interfaces/total-extraction';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';
import { map } from 'rxjs/operators';
import { UserDataService } from '../../../core/services/parameter/user-data/user-data.service';
import { CacheService } from '../../../../../../projects/common/src/app/services/cache.service';
import { CommunicationService } from 'src/app/services/communication';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';

@Injectable({
  providedIn: 'root',
})
export class TotalExtractionService {
  private baseUrl: string;
  constructor(
    private userData: UserDataService,
    private parameterService: ParameterHandlerService,
    private communicationService: CommunicationService,
    private cacheService: CacheService,
    private urlBuilderService: UrlBuilderService,
  ) {
    this.baseUrl = this.urlBuilderService.serverBasePath;
  }

  /**
   * Function to get total, scheduled & remnant loops
   * @param inputDate Date to get loop
   * @param shiftId Shift ID to get loops
   */
  public getProgressBarLoops(inputDate: string, shiftId: number): any {
    const params = this.setParams(inputDate, shiftId);

    const totalLoopByShift$ = this.communicationService
      .get(`${this.baseUrl}/Production/GetTotalLoopByShift`, params)
      .pipe(map((resp) => (resp ? resp.totalLoop : '0')));

    const scheduledLoopByShift$ = this.communicationService
      .get(`${this.baseUrl}/Production/GetScheduledLoopByShift`, params)
      .pipe(map((resp) => (resp ? resp.scheduledLoop : '0')));

    const remnantsLoopByShift$ = this.communicationService
      .get(`${this.baseUrl}/Production/GetRemnantsLoopByShift`, params)
      .pipe(map((resp) => (resp ? resp.remnantsLoop : '0')));

    return forkJoin(totalLoopByShift$, scheduledLoopByShift$, remnantsLoopByShift$);
  }

  /**
   * Function to get first loop from each shift
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  public getFirstLoop(inputDate: string, shiftId: number): Observable<ILoopDetail[]> {
    const params = this.setParams(inputDate, shiftId);

    return this.communicationService
      .get(`${this.baseUrl}/Production/GetFirstLoopByShift`, params)
      .pipe(map((resp) => resp || ([] as ILoopDetail[])));
  }

  /**
   * Function to get last loop from each shift
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  public getLastLoop(inputDate: string, shiftId: number): Observable<ILoopDetail[]> {
    const params = this.setParams(inputDate, shiftId);

    return this.communicationService
      .get(`${this.baseUrl}/Production/GetLastLoopByShift`, params)
      .pipe(map((resp) => resp || ([] as ILoopDetail[])));
  }

  /**
   * Function to get first loop detail table data by shift
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  public getFirstLoopDetailByShiftCompany(inputDate: string, shiftId: number, rangeShift: string): Observable<ILoopDetailTable[]> {
    const params = this.setLoopDetailTableParams(inputDate, shiftId, rangeShift);

    return this.communicationService.get(`${this.baseUrl}/Production/GetLoopDetailByShift`, params).pipe(map(this.loopDetailMapper));
  }

  /**
   * Function to get loop detail table data
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  public getLastLoopDetailByShiftCompany(inputDate: string, shiftId: number, rangeShift: string): Observable<ILoopDetailTable[]> {
    const params = this.setLoopDetailTableParams(inputDate, shiftId, rangeShift);

    return this.communicationService.get(`${this.baseUrl}/Production/GetLoopDetailByShift`, params).pipe(
      map((resp) => {
        return this.loopDetailMapper(resp);
      }),
    );
  }

  /**
   * Function to map objects from loop detail HTTP request to loop detail table
   */
  private loopDetailMapper(loopDetailArray: IFirstLastLoopResponse[]): ILoopDetailTable[] {
    const mappedArray: ILoopDetailTable[] = [];

    for (const loop of loopDetailArray) {
      const loopDetail = {
        isFirstList: loop.isFirstList,
        machine_name: loop.machineName,
        operator: loop.operator,
        extraction: loop.startZone,
        dump: loop.endZone,
        finishTime: loop.finishedAt,
      } as ILoopDetailTable;

      mappedArray.push(loopDetail);
    }

    return mappedArray;
  }

  /**
   * Function to set params to build query
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private setParams(inputDate: string, shiftId: number): any {
    return {
      inputDate,
      shiftId: shiftId.toString(),
      companyId: this.getUserCompany(),
      zoneId: this.cacheService.session('selectedZone').zoneId,
    };
  }

  /**
   * Function to set params to build query
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private setLoopDetailTableParams(inputDate: string, shiftId: number, rangeShift: string): any {
    const currentMachine = this.parameterService.parameters.item('machine-context').value as IMachineContext;
    return {
      inputDate,
      shiftId: shiftId.toString(),
      companyId: this.getUserCompany(),
      zoneId: this.cacheService.session('selectedZone').zoneId,
      machineTypeId: currentMachine.machineTypeId,
      range: rangeShift,
    };
  }

  /**
   * Function to get user company ID
   */
  public getUserCompany(): string {
    return (this.userData.company() ? this.userData.company().id : 0).toString();
  }
}
